import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { css, SerializedStyles } from '@emotion/core';
import { Z_INDEX } from '../globalStyle/zIndex';
import * as Localization from '../localization/module';
import LocalDiv from '../localization/LocalDiv';
import { RTL_SHADOW, LTR_SHADOW } from '../globalStyle/shadows';
import { COLORS } from '../globalStyle/colors';
import { faBars, faChevronLeft, faChevronRight, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HIDE_ON_MOBILE, onMobileDevice } from '../globalStyle/mediaQueries';
import { useAppState } from '../core/redux/hooks';
import { useDispatch } from 'react-redux';
import { openMenuAction } from '../project/sideMenu/openMenuAction';


const styles = {
  root:               css`
    position: fixed;
    top: 0;
    height: 100vh;
    z-index: ${Z_INDEX.SIDE_MENU};
    
    display: flex;
    flex-direction: row;
    transition: transform .5s ease;
  `,
  displaceDiv:        css`
    flex-shrink: 0;
    height: 1px;
    transition: all .5s ease;
  `,
  menuContentWrapper: css`
    background-color: ${COLORS.WHITE};
  `,
  openCloseBtns:      css`
    height: 50vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    ${onMobileDevice} {
      width: 0;
      height: 50px;
    }
  `,
  hamburgerBtn:       css`
    height: 48px;
    width: 50px;
    background-color: ${COLORS.WHITE};
    border-radius: 0;
    border: none;
    padding: 10px;
    font-size: 18px;
    
    &:hover { 
      cursor: pointer;
      color: ${COLORS.BLUE}
    }
  `,
  chevronBtn:         css`
    height: 40px;
    width: 40px;
    font-size: 15px;
    background-color: ${COLORS.WHITE};
    border-radius: 100%;
    border: none;
    transition: padding, margin .5s ease;
    
    /* 
      This button is a circle, centered-ish on the edge of the side menu. 
      We want the part outside the menu; but we don't want the part inside the menu to overlap the menu contents. 
      So, we clip off the part of the button on the inside of the menu. 
    */
    &[dir="ltr"] {
      clip-path: inset(0px 0px 0px 14px);
    }
    
    &[dir="rtl"] {
      clip-path: inset(0px 14px 0px 0px);
    }
    
    margin-left: -24px;
    padding: 0 0 0 10px;
    
    &:hover { 
      cursor: pointer;
      color: ${COLORS.BLUE}
    }
    
    &:focus { 
      outline: none; /* Sorry visually-impaired people, but you get the hamburger button */
    }
    
    /* When the menu is especially short, this little tab button thing encroaches on the the hamburger button */
    @media only screen and (max-height: 500px) {
      display: none;
    } 
  `,
};


const getChevronInset = (isOpen: boolean): string => isOpen ? '-24px' : '-14px';


const getChevronOffset = (
  isOpen: boolean,
  isRtl: boolean,
): SerializedStyles => isRtl
  ? css` margin-right: ${getChevronInset(isOpen)}; padding: 0 10px 0 0;`
  : css` margin-left: ${getChevronInset(isOpen)}; padding: 0 0 0 10px;`;


const getChevronImg = (
  isOpen: boolean,
  isRtl: boolean,
): IconDefinition => isOpen
  ? isRtl ? faChevronRight : faChevronLeft
  : isRtl ? faChevronLeft : faChevronRight;


const getShadow = (
  isOpen: boolean,
  isRtl: boolean,
): SerializedStyles | undefined => isOpen ? isRtl ? RTL_SHADOW : LTR_SHADOW : undefined;


type Props = {
  /**
   * Whether or not the menu should displace sibling content in the DOM where it's added
   */
  displace?: boolean;

  /**
   * The width of the content in the menu (buttons hanging off the menu are an extra 50px)
   */
  contentWidthPx: number;

  /**
   * Whether or not the menu should start in the open position
   */
  startOpen?: boolean;

  closeOnClickInside?: boolean;

  className?: string;
};


/**
 * SideMenu: a component that renders a menu on the side of the screen (which side depends on the user's locale).
 * This component doesn't have any content itself, you should wrap your menu content with this component to render it
 * in a fancy pull-out menu on the side of the screen
 *
 * If using displacement option (props.displace), you will need to put this menu and its siblings in a LocalDiv.
 *
 * PureComponent: true
 * Redux Connected: true
 */
const SideMenu: React.FC<Props> = props => {
  const dispatch = useDispatch();

  const isRtl  = useAppState(state => Localization.isRtl(state.locale.textDirection));
  const isOpen = useAppState(state => state.openSideMenu);

  const toggleMenu        = React.useCallback(() => dispatch(openMenuAction(!isOpen)), [isOpen, dispatch]);
  const toggleMenuDelayed = React.useCallback(() => window.setTimeout(toggleMenu, 250), [toggleMenu]);

  const shadow        = React.useMemo(() => getShadow(isOpen, isRtl), [isOpen, isRtl]);
  const chevronOffset = React.useMemo(() => getChevronOffset(isOpen, isRtl), [isOpen, isRtl]);

  const menuOffset = React.useMemo(
    () => isOpen ? 0 : isRtl ? props.contentWidthPx : -props.contentWidthPx,
    [props.contentWidthPx, isRtl, isOpen],
  );

  const menuDisplacement = React.useMemo(() => isOpen ? props.contentWidthPx : 0, [props.contentWidthPx, isOpen]);

  const onClick = React.useCallback(
    (
      event: React.MouseEvent<HTMLElement>,
    ) => props.closeOnClickInside
         && isOpen
         && (event.target as HTMLElement | undefined)?.tagName.toLowerCase() !== 'div'
         && toggleMenuDelayed(),
    [props.closeOnClickInside, isOpen, toggleMenuDelayed],
  );

  return (
    <React.Fragment>
      { // Div to sit in the DOM where the menu was added and displace content (if props says to do so)
        props.displace && (
          <div
            css={[
              styles.displaceDiv,
              css`width: ${menuDisplacement}px; flex-basis: ${menuDisplacement}px;`,
            ]}
          />
        )
      }
      { // Menu
        ReactDOM.createPortal(
          <LocalDiv
            aria-expanded={isOpen}
            role='menu'
            css={[
              styles.root,
              css`
                ${isRtl ? 'right' : 'left'}: 0;
                transform: translateX(${menuOffset}px);
              `,
            ]}
          >
            {/* Menu Content */}
            <div
              css={[styles.menuContentWrapper, shadow, css`width: ${props.contentWidthPx}px;`]}
              onClick={onClick}
              className={props.className}
            >
              {
                props.children
              }
            </div>

            {/* Open/Close Buttons */}
            <div css={styles.openCloseBtns}>
              <button css={[shadow, styles.hamburgerBtn]} onClick={toggleMenu}>
                <FontAwesomeIcon icon={faBars} />
              </button>
              <button
                css={[styles.chevronBtn, chevronOffset, HIDE_ON_MOBILE]}
                onClick={toggleMenu}
                dir={isRtl ? 'rtl' : 'ltr'}
                tabIndex={-1}
              >
                <FontAwesomeIcon icon={getChevronImg(isOpen, isRtl)} />
              </button>
            </div>
          </LocalDiv>,
          document.getElementById('root') || document.getElementById('react-root') || document.body,
        )
      }
    </React.Fragment>
  );
};

export default SideMenu;
